<template>
	<div>
		<div class="list_top_box">
			<h3>Current Stock</h3>
			<p class="mgb_5">
				<span>As of: {{ as_of_date }}</span>
				<span style="float: right;"><b>Currency: $</b></span>
			</p>
		</div>
		<div style="overflow: auto; min-height: 320px; max-height: 360px;">
			<div class="col_6" style="padding-right: 10px;">
				<h4 class="mgb_5">By Collection</h4>
				<table class="tbl_list">
					<thead>
						<tr>
							<th class="al_left">Collection</th>
							<th class="al_right">QTY</th>
							<th class="al_right">Stock Value ($)</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in collection_rows" :key="row.collection">
							<td class="al_left">{{ row.collection }}</td>
							<td class="al_right">{{ row.qty | commaSeparated }}</td>
							<td class="al_right">{{ row.stock_value | toCurrency }}</td>
						</tr>
						<tr v-if="collection_rows.length > 0" class="bg_gray">
							<td class="al_left"><b>TOTAL</b></td>
							<td class="al_right">
								<b>{{ collection_total.qty | commaSeparated }}</b>
							</td>
							<td class="al_right">
								<b>{{ collection_total.stock_value | toCurrency }}</b>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="col_6" style="padding-left: 10px;">
				<h4 class="mgb_5">By Category</h4>
				<table class="tbl_list">
					<thead>
						<tr>
							<th class="al_left">Category</th>
							<th class="al_right">QTY</th>
							<th class="al_right">Stock Value ($)</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in category_rows" :key="row.category">
							<td class="al_left">{{ row.category }}</td>
							<td class="al_right">{{ row.qty | commaSeparated }}</td>
							<td class="al_right">{{ row.stock_value | toCurrency }}</td>
						</tr>
						<tr v-if="category_rows.length > 0" class="bg_gray">
							<td class="al_left"><b>TOTAL</b></td>
							<td class="al_right">
								<b>{{ category_total.qty | commaSeparated }}</b>
							</td>
							<td class="al_right">
								<b>{{ category_total.stock_value | toCurrency }}</b>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
		<!-- #3366 재고 연령. 위 두 표의 스크롤 영역 밖에 두어 표와 범례 전체가 잘리지 않고 보이게 한다. -->
		<div v-if="age_category_rows.length > 0" class="inventory_age">
			<h4 class="mgb_5">Inventory Age</h4>
			<p class="age_note">Each item's full on-hand QTY and value is placed in one range by the estimated age of its oldest remaining PO receipt (FIFO).</p>
			<table class="tbl_list">
				<thead>
					<tr>
						<th rowspan="2" class="al_left">Category / Collection</th>
						<th v-for="range in age_ranges" :key="range.key" colspan="2" :class="['al_center', range.css]">
							<span :class="['age_dot', range.css]"></span>
							{{ range.label }}
						</th>
					</tr>
					<tr>
						<template v-for="range in age_ranges">
							<th :key="range.key + '_qty'" class="al_right">QTY</th>
							<th :key="range.key + '_value'" class="al_right">Value ($)</th>
						</template>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in age_category_rows" :key="'category_' + row.name" class="age_category_row">
						<td class="al_left">{{ row.name }}</td>
						<template v-for="range in age_ranges">
							<td :key="range.key + '_qty'" class="al_right">{{ row['qty_' + range.key] | commaSeparated }}</td>
							<td :key="range.key + '_value'" class="al_right">{{ row['value_' + range.key] | toCurrency }}</td>
						</template>
					</tr>
					<tr v-for="(row, index) in age_collection_rows" :key="'collection_' + row.name" :class="['age_collection_row', { age_group_start: index === 0 }]">
						<td class="al_left">{{ row.name }}</td>
						<template v-for="range in age_ranges">
							<td :key="range.key + '_qty'" class="al_right">{{ row['qty_' + range.key] | commaSeparated }}</td>
							<td :key="range.key + '_value'" class="al_right">{{ row['value_' + range.key] | toCurrency }}</td>
						</template>
					</tr>
					<tr class="bg_gray age_total_row">
						<td class="al_left"><b>TOTAL</b></td>
						<template v-for="range in age_ranges">
							<td :key="range.key + '_qty'" class="al_right">
								<b>{{ age_total['qty_' + range.key] | commaSeparated }}</b>
							</td>
							<td :key="range.key + '_value'" class="al_right">
								<b>{{ age_total['value_' + range.key] | toCurrency }}</b>
							</td>
						</template>
					</tr>
				</tbody>
			</table>
			<p class="age_legend">
				<span class="age_dot age_gray"></span>
				Undetermined: on hand exceeds the PO receipts recorded in HERP up to today (e.g. stock from before HERP, non-PO additions, adjustments), so age cannot be determined from PO history.
			</p>
		</div>
	</div>
</template>

<script>
import { apiGetCurrentStockArkin } from '@/api/statistics/statistics.js';
import { today } from '@/utils/date.js';

// #3366 재고 연령 구간. key 는 API 필드 접미사(qty_age_<key>, value_age_<key>)다.
const AGE_RANGES = [
	{ key: '0_90', label: '0–90 days', css: 'age_green' },
	{ key: '91_180', label: '91–180 days', css: 'age_amber' },
	{ key: '181_plus', label: '181+ days', css: 'age_red' },
	{ key: 'undetermined', label: 'Undetermined', css: 'age_gray' },
];

export default {
	data: () => ({
		rows: [],
		as_of_date: today(),
		age_ranges: AGE_RANGES,
	}),
	computed: {
		category_rows() {
			return this.rows.map(r => ({
				category: r.category,
				qty: Number(r.qty) || 0,
				stock_value: Number(r.stock_value) || 0,
			}));
		},
		collection_rows() {
			const map = {};
			for (const r of this.rows) {
				const key = r.collection;
				if (!map[key]) map[key] = { collection: key, qty: 0, stock_value: 0 };
				map[key].qty += Number(r.qty) || 0;
				map[key].stock_value += Number(r.stock_value) || 0;
			}
			const order = ['TOUCH', 'CATCH'];
			return Object.values(map).sort((a, b) => order.indexOf(a.collection) - order.indexOf(b.collection));
		},
		category_total() {
			return this.category_rows.reduce((acc, r) => ({ qty: acc.qty + r.qty, stock_value: acc.stock_value + r.stock_value }), { qty: 0, stock_value: 0 });
		},
		collection_total() {
			return this.collection_rows.reduce((acc, r) => ({ qty: acc.qty + r.qty, stock_value: acc.stock_value + r.stock_value }), { qty: 0, stock_value: 0 });
		},
		// #3366 행 순서는 By Category, By Collection 표와 같다. 합계는 원 값으로 더하고 표시할 때만 반올림한다.
		age_category_rows() {
			return this.rows.map(r => this.ageRow(r.category, [r]));
		},
		age_collection_rows() {
			return this.collection_rows.map(c => {
				const source_rows = this.rows.filter(r => r.collection === c.collection);
				return this.ageRow(c.collection, source_rows);
			});
		},
		age_total() {
			return this.ageRow('TOTAL', this.rows);
		},
	},
	methods: {
		ageRow(name, source_rows) {
			const age_row = { name };
			AGE_RANGES.forEach(range => {
				age_row['qty_' + range.key] = source_rows.reduce((sum, r) => sum + (Number(r['qty_age_' + range.key]) || 0), 0);
				age_row['value_' + range.key] = source_rows.reduce((sum, r) => sum + (Number(r['value_age_' + range.key]) || 0), 0);
			});
			return age_row;
		},
		async loadData() {
			const { data } = await apiGetCurrentStockArkin();
			this.rows = Array.isArray(data) ? data : [];
		},
	},
	created() {
		this.loadData();
	},
};
</script>

<style scoped>
.tbl_list {
	width: 100%;
	border-collapse: collapse;
}
.tbl_list th,
.tbl_list td {
	border: 1px solid #ddd;
	padding: 6px 8px;
}
.tbl_list thead th {
	background: #f5f7fa;
}
.bg_gray {
	background: #f5f7fa;
}
.inventory_age {
	clear: both;
	padding: 10px 10px 0;
}
.age_note,
.age_legend {
	color: #666;
	font-size: 12px;
	line-height: 1.5;
}
.age_note {
	margin-bottom: 5px;
}
.age_legend {
	margin-top: 5px;
}
.age_dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 4px;
	border-radius: 50%;
	vertical-align: middle;
}
.age_dot.age_green {
	background: #2e9e44;
}
.age_dot.age_amber {
	background: #f0ad00;
}
.age_dot.age_red {
	background: #d9363e;
}
.age_dot.age_gray {
	background: #9aa0a6;
}
.tbl_list thead th.age_green {
	background: #e8f5e9;
}
.tbl_list thead th.age_amber {
	background: #fff8e1;
}
.tbl_list thead th.age_red {
	background: #fdecea;
}
.tbl_list thead th.age_gray {
	background: #eef0f2;
}
.tbl_list tr.age_group_start td {
	border-top: 2px solid #bbb;
}
</style>
